import { memo, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import classNames from "classnames";
import { Icons } from "../Icons";
import { formatClock, pad, receivedDayPrefix } from "../../lib/format";
import { useDataFreshness } from "../../hooks/useDataFreshness";
import { screenLabel } from "../../lib/nav";

// Isolé et mémoïsé : l'horloge rerend la barre chaque seconde, la fraîcheur
// n'a pas à se recalculer pour autant. Lecture du cache seule : la barre ne
// charge rien, c'est /recap qui ramène les timelines de l'équipe.
const DataFreshnessLabel = memo(function DataFreshnessLabel() {
  const receivedAt = useDataFreshness();
  if (!receivedAt) return null;
  return (
    <span className="net-fresh">
      · Données {receivedDayPrefix(receivedAt)}
      {formatClock(receivedAt)}
    </span>
  );
});

interface Props {
  online: boolean;
  pendingSync: number;
  attendanceOpen: boolean;
  onBadge: () => void;
  onMenuToggle: () => void;
}

export function Topbar({
  online,
  pendingSync,
  attendanceOpen,
  onBadge,
  onMenuToggle,
}: Props) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const crumb = screenLabel(useLocation().pathname);

  const timeStr = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

  return (
    <div className="topbar">
      <button
        className="menu-toggle"
        onClick={onMenuToggle}
        aria-label="Menu"
        type="button"
      >
        <Icons.list size={18} sw={1.8} />
      </button>

      <div className="crumb">
        <span>Chronos</span>
        <Icons.chev size={12} sw={2} />
        <span className="cur">{crumb}</span>
      </div>

      <div className="spacer-flex" />

      <div className="now-time">{timeStr}</div>

      <button
        type="button"
        className={classNames("net-chip", { off: !online })}
      >
        <span className="dot" />
        <span>{online ? "En ligne" : "Hors ligne"}</span>
        <DataFreshnessLabel />
        {pendingSync > 0 && (
          <span style={{ marginLeft: 4, fontWeight: 600 }}>
            · {pendingSync}
          </span>
        )}
      </button>

      <button
        type="button"
        className={classNames("quick-badge", attendanceOpen ? "out" : "in")}
        onClick={onBadge}
      >
        {attendanceOpen ? (
          <Icons.arrowOut size={16} sw={2.2} />
        ) : (
          <Icons.arrowIn size={16} sw={2.2} />
        )}
        <span>{attendanceOpen ? "Badger sortie" : "Badger entrée"}</span>
      </button>
    </div>
  );
}
